import { TODO } from '@b2b/types/core';
import angular from 'angular';
import { Subject } from 'rxjs';
import { AdvancedFiltersVM, RawFilters, AdvancedFilterField } from './types';

export class AdvancedFiltersService {
  static $inject = [];

  componentsMap: Record<string, AdvancedFiltersVM> = {};

  registerComponent(props: { name: string; triggerCtrl?: TODO; filtersCtrl?: TODO }) {
    if (!this.componentsMap[props.name]) {
      this.componentsMap[props.name] = {
        name: props.name,
        triggerCtrl: undefined,
        filtersCtrl: undefined,
        model: {},
        subject: new Subject<void>(),
      };
    }

    if (props.triggerCtrl) {
      this.componentsMap[props.name].triggerCtrl = props.triggerCtrl;
    }

    if (props.filtersCtrl) {
      this.componentsMap[props.name].filtersCtrl = props.filtersCtrl;
    }

    return this.componentsMap[props.name];
  }

  unregisterComponent(props: { name: string }) {
    if (this.componentsMap[props.name]) {
      delete this.componentsMap[props.name];
    }
  }

  clearComponentFilter(props: { name: string; filterName: string }) {
    const vm = this.componentsMap[props.name];

    if (!vm) {
      return;
    }

    vm.filtersCtrl.clearFilter(props.filterName);
  }

  clearAllComponentFilters(props: { name: string; emitEvent: boolean }) {
    const vm = this.componentsMap[props.name];

    if (!vm) {
      return;
    }

    vm.filtersCtrl.clearAllFilters({ emitEvent: props.emitEvent });
  }

  updateFromRawFilters(props: { name: string; filters: RawFilters }) {
    const vm = this.componentsMap[props.name];

    if (!vm) {
      return;
    }

    vm.filtersCtrl.updateFromRawFilters(props.filters);
  }

  mapCustomFieldsToAdvancedFiltersFields(
    customFields: Array<{
      ndx: number;
      label: string;
      format: 'Text' | 'Dropdown';
      values: string[];
      internalLabel: string;
    }>
  ): Array<{
    type: 'section' | 'field';
    ndx: number;
    label: string;
    format: 'Text' | 'Dropdown';
    selectable?: Array<{ label: string; value: string; checked: boolean }>;
    value: string;
  }> {
    return customFields.map((customField) => {
      const result: TODO = {
        type: 'field',
        ndx: customField.ndx,
        label: customField.label,
        format: customField.format,
        value: customField.internalLabel,
      };

      if (customField.format === 'Dropdown') {
        const selectable = customField.values.map((value) => {
          return {
            label: value,
            value: value,
            checked: false,
          };
        });

        result.selectable = selectable;
      }

      return result;
    });
  }

  mapRawFiltersToFieldsSearchParams(rawFilters: RawFilters): Array<{ name: string; label: string; values: Array<string> }> {
    return Object.keys(rawFilters).reduce((result, key) => {
      const rawValue = rawFilters[key];

      if (!rawValue || (Array.isArray(rawValue) && !rawValue.length)) {
        return result;
      }

      if (Array.isArray(rawValue)) {
        const values = rawValue.map((item) => {
          return item.value;
        });
        result.push({
          name: key,
          label: values.join(', '),
          values: values,
        });

        return result;
      }

      if (typeof rawValue === 'object') {
        result.push({
          name: key,
          label: rawValue.label,
          values: [rawValue.value],
        });

        return result;
      }

      if (typeof rawValue === 'boolean') {
        result.push({
          name: key,
          label: rawValue ? 'Yes' : 'No',
          values: [rawValue],
        });

        return result;
      }
      
      const values = rawValue
        .split(',')
        .map((value) => {
          return value.trim();
        })
        .filter(Boolean);

      result.push({
        name: key,
        label: values.join(', '),
        values,
      });

      return result;
    }, [] as TODO[]);
  }
  
  disableField(props: { componentName: string, field: AdvancedFilterField }) {
    if (props.field.disableField) {
      return props.field.disableField(this.componentsMap[props.componentName].model);
    }

    return false;
  }

  hideField(props: { componentName: string, field: AdvancedFilterField }) {
    if (props.field.hideField) {
      return props.field.hideField(this.componentsMap[props.componentName].model);
    }

    return false;
  }
}

export const AdvancedFiltersServiceModule = angular
  .module('acme.common.service.advancedFilters', [])
  .service('AdvancedFiltersService', AdvancedFiltersService).name;
